import React, { useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import {
  Plane,
  Truck,
  Zap,
  Globe,
  CheckCircle,
  ArrowRight,
  Package,
  Clock,
  Shield,
  MapPin,
} from "lucide-react";
import planeAir from "@/assets/images/plane-air.jpg";
import planeCargo1 from "@/assets/images/plane-cargo1.jpg";
import planeCargo2 from "@/assets/images/plane-cargo2.jpg";
import ban1 from "@/assets/images/f4.jpeg";
import { useTheme } from "@/context/theme-context";
import { useModalContext } from "@/context/modal-context";

const services = [
  {
    id: "air-freight",
    icon: Plane,
    label: "Air Freight",
    tagline: "Fast. Reliable. Worldwide.",
    description:
      "Our air freight service offers the fastest transit times for your cargo, connecting you to destinations across the globe with precision and care. Whether it's urgent documents, electronics, or heavy machinery, we handle every shipment with the attention it deserves.",
    image: planeAir,
    color: "#1e40af",
    highlights: [
      "Express door-to-door delivery",
      "3–5 business day international transit",
      "Full customs clearance support",
      "Real-time shipment tracking",
      "Cargo insurance options",
      "Dangerous goods handling",
    ],
    stats: [
      { value: "3–5", label: "Days transit" },
      { value: "100+", label: "Destinations" },
      { value: "24/7", label: "Support" },
    ],
  },
  {
    id: "ground-shipping",
    icon: Truck,
    label: "Ground Shipping",
    tagline: "Dependable. Cost-Effective. Door-to-Door.",
    description:
      "Our ground shipping network ensures safe, cost-effective transport for domestic and regional shipments. From small parcels to full truck loads, we coordinate collection, transit, and delivery so you don't have to.",
    image: planeCargo1,
    color: "#dc2626",
    highlights: [
      "Nationwide domestic coverage",
      "Less-than-truckload (LTL) & full truckload",
      "Scheduled pickup & delivery windows",
      "Fragile and oversized cargo welcome",
      "Temperature-controlled options",
      "Proof of delivery confirmation",
    ],
    stats: [
      { value: "5–10", label: "Days domestic" },
      { value: "50+", label: "Cities covered" },
      { value: "99%", label: "On-time rate" },
    ],
  },
  {
    id: "express-delivery",
    icon: Zap,
    label: "Express Delivery",
    tagline: "When Every Hour Counts.",
    description:
      "Time-critical shipments demand a premium, priority service. Our express delivery lane fast-tracks your cargo through every stage — from pickup to final-mile — with dedicated handling and priority loading on the next available flight or vehicle.",
    image: planeCargo2,
    color: "#7c3aed",
    highlights: [
      "Next-day & same-day options",
      "Priority loading on all routes",
      "Dedicated account manager",
      "Proactive status notifications",
      "Guaranteed delivery windows",
      "Secure chain-of-custody handling",
    ],
    stats: [
      { value: "24h", label: "Express turnaround" },
      { value: "100%", label: "Priority handled" },
      { value: "Live", label: "Tracking updates" },
    ],
  },
  {
    id: "international",
    icon: Globe,
    label: "International Shipping",
    tagline: "The World is Your Warehouse.",
    description:
      "Trade without borders. Our international shipping solution covers import and export across continents — managing customs declarations, duties, and documentation so your goods move seamlessly from origin to destination, wherever in the world that may be.",
    image: ban1,
    color: "#059669",
    highlights: [
      "Import & export management",
      "Customs brokerage & documentation",
      "Multi-modal (air + road + sea) options",
      "Competitive freight rates",
      "HS code classification support",
      "Duty drawback assistance",
    ],
    stats: [
      { value: "50+", label: "Countries served" },
      { value: "All", label: "Modes of transport" },
      { value: "Zero", label: "Hidden fees" },
    ],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

interface ServiceSectionProps {
  service: (typeof services)[0];
  index: number;
}

const ServiceSection: React.FC<ServiceSectionProps> = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const { isDark } = useTheme();
  const { openContactModal } = useModalContext();
  const Icon = service.icon;
  const isEven = index % 2 === 0;

  return (
    <section
      id={service.id}
      ref={ref}
      className="relative py-16 sm:py-20 lg:py-28 overflow-hidden"
      style={{
        background:
          index % 2 === 0
            ? isDark
              ? "#0f172a"
              : "#ffffff"
            : isDark
              ? "#1e293b"
              : "#f8fafc",
      }}
    >
      {/* Subtle background orb */}
      <div
        className="absolute pointer-events-none rounded-full blur-[120px] opacity-20"
        style={{
          width: 500,
          height: 500,
          background: service.color,
          top: "50%",
          [isEven ? "left" : "right"]: "-10%",
          transform: "translateY(-50%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
            isEven ? "" : "lg:[&>*:first-child]:order-2"
          }`}
        >
          {/* Image */}
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="relative rounded-3xl overflow-hidden shadow-2xl"
            style={{ aspectRatio: "4/3" }}
          >
            <img
              src={service.image}
              alt={service.label}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, ${service.color}33 0%, transparent 60%)`,
              }}
            />
            {/* Floating stat chips */}
            <div className="absolute bottom-4 left-4 right-4 flex gap-2 flex-wrap">
              {service.stats.map((s, i) => (
                <div
                  key={i}
                  className="px-3 py-1.5 rounded-xl backdrop-blur-md text-white text-center"
                  style={{ background: "rgba(0,0,0,0.5)" }}
                >
                  <div className="text-base font-extrabold leading-none">
                    {s.value}
                  </div>
                  <div className="text-[10px] opacity-75 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Content */}
          <div className="space-y-6">
            <motion.div
              custom={1}
              variants={fadeUp}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-semibold mb-4"
                style={{
                  background: `${service.color}15`,
                  borderColor: `${service.color}30`,
                  color: service.color,
                }}
              >
                <Icon className="w-4 h-4" />
                <span>{service.label}</span>
              </div>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-3"
                style={{
                  fontFamily: "var(--font-heading)",
                  color: "var(--text-primary)",
                }}
              >
                {service.tagline}
              </h2>
              <p
                className="text-base sm:text-lg leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {service.description}
              </p>
            </motion.div>

            <motion.ul
              custom={2}
              variants={fadeUp}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
            >
              {service.highlights.map((h, i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <CheckCircle
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: service.color }}
                  />
                  <span
                    className="text-sm"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {h}
                  </span>
                </li>
              ))}
            </motion.ul>

            <motion.button
              custom={3}
              variants={fadeUp}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              onClick={openContactModal}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white"
              style={{
                background: `linear-gradient(135deg, ${service.color}, #dc2626)`,
              }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              Get a Quote
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesPage: React.FC = () => {
  const { isDark } = useTheme();
  const { openContactModal } = useModalContext();
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true });

  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        setTimeout(() => {
          document
            .getElementById(hash)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 120);
      }
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  return (
    <div style={{ background: "var(--bg-primary)" }}>
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[55vh] flex items-center justify-center overflow-hidden pt-24 pb-16"
        style={{ background: isDark ? "#0f172a" : "#ffffff" }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-[130px]"
            style={{
              background: isDark
                ? "rgba(30,64,175,0.15)"
                : "rgba(30,64,175,0.08)",
            }}
            animate={{ x: [0, 60, 0], y: [0, -30, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-[120px]"
            style={{
              background: isDark
                ? "rgba(220,38,38,0.12)"
                : "rgba(220,38,38,0.07)",
            }}
            animate={{ x: [0, -50, 0], y: [0, 40, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-semibold mb-6"
              style={{
                background: isDark
                  ? "rgba(30,41,59,0.6)"
                  : "rgba(255,255,255,0.8)",
                borderColor: isDark
                  ? "rgba(59,130,246,0.25)"
                  : "rgba(59,130,246,0.2)",
                color: "var(--primary-blue-light)",
                backdropFilter: "blur(12px)",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              <span>What We Offer</span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-5 leading-tight"
              style={{
                fontFamily: "var(--font-heading)",
                color: "var(--text-primary)",
              }}
            >
              Our{" "}
              <span className="bg-gradient-to-r from-blue-600 via-red-500 to-blue-700 bg-clip-text text-transparent">
                Services
              </span>
            </h1>
            <p
              className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-8"
              style={{ color: "var(--text-secondary)" }}
            >
              From urgent express parcels to large international freight,
              Seventh Air offers end-to-end shipping solutions built around your
              needs.
            </p>

            {/* Quick jump pills */}
            <div className="flex flex-wrap justify-center gap-2">
              {services.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium border transition-all hover:scale-105"
                  style={{
                    background: isDark
                      ? "rgba(30,41,59,0.5)"
                      : "rgba(255,255,255,0.7)",
                    borderColor: isDark
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.08)",
                    color: "var(--text-secondary)",
                  }}
                >
                  <s.icon className="w-3.5 h-3.5" />
                  {s.label}
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service sections */}
      {services.map((service, index) => (
        <ServiceSection key={service.id} service={service} index={index} />
      ))}

      {/* CTA Banner */}
      <section
        className="py-16 sm:py-20 relative overflow-hidden"
        style={{ background: isDark ? "#0f172a" : "#ffffff" }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, #1e40af10 0%, #dc262610 100%)",
          }}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex justify-center gap-4 mb-6">
            {[Package, Clock, Shield, MapPin].map((Icon, i) => (
              <div
                key={i}
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #1e40af20, #dc262620)",
                }}
              >
                <Icon
                  className="w-5 h-5"
                  style={{ color: "var(--primary-blue-light)" }}
                />
              </div>
            ))}
          </div>
          <h2
            className="text-3xl sm:text-4xl font-extrabold mb-4"
            style={{
              fontFamily: "var(--font-heading)",
              color: "var(--text-primary)",
            }}
          >
            Not sure which service fits?
          </h2>
          <p
            className="text-base sm:text-lg mb-8"
            style={{ color: "var(--text-secondary)" }}
          >
            Our team will assess your shipment and recommend the best solution —
            always at a competitive price.
          </p>
          <motion.button
            onClick={openContactModal}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white text-base"
            style={{
              background: "linear-gradient(135deg, #1e40af 0%, #dc2626 100%)",
            }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            Talk to Our Team
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
